import { useAuth } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { configureAPI } from '@uturn/api';
import { isAxiosError } from 'axios';
import { type FC, type PropsWithChildren, useEffect } from 'react';
import { queryClient } from '../query';

let isSigningOut = false;

const useConfigureHttpClient = () => {
	const { getToken, signOut } = useAuth();

	useEffect(() => {
		configureAPI({
			portal: {
				url: import.meta.env.VITE_API_URL,
				key: import.meta.env.VITE_API_KEY,
			},
			auth: {
				url: import.meta.env.VITE_API_AUTH_URL,
				key: import.meta.env.VITE_API_AUTH_KEY,
			},
			portalInterceptors: {
				request: [
					async (config) => {
						const token = await getToken();

						if (token) {
							config.headers.Authorization = `Bearer ${token}`;
						}
						config.headers['x-uturn-origin'] = 'NEXTURN';

						return config;
					},
				],
				response: [
					(response) => response,
					async (error) => {
						if (isAxiosError(error)) {
							const status = error.response?.status;
							if (status === 401 && !isSigningOut) {
								isSigningOut = true;
								try {
									await signOut();
								} finally {
									isSigningOut = false;
								}
								window.location.href = '/sign-in';
							}
						}
						return Promise.reject(error);
					},
				],
			},
		});
	}, [getToken, signOut]);
};

export const HttpClientProvider: FC<PropsWithChildren> = ({ children }) => {
	useConfigureHttpClient();

	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
};
